import { parseJSONWithStringNumbers } from "@/flow/application/jsonParseForNumbers";
import {
  getTooltipforOutput,
  getTooltipForMetaoutput,
  getTooltipForUdv,
} from "@/flow/application/useTooltipForTags";
import { labelConvert } from "@/core/util/variable-label-convert";
import {
  getType,
  hasChild,
  createHTMLSpanElement,
  setTagDisplayText,
  validateOutputTag,
  validateMetaoutputTag,
} from "./service";

export interface SpanParams {
  name: string;
  path: string;
  stepIndex: number;
  stepNumber?: number;
  tooltip?: string;
  origId?: string;
}

export function addOutput(
  params: SpanParams,
  outputList: any[],
  count: number,
  isNotFirstRender = true
): HTMLSpanElement {
  if (params.stepIndex < 0) throw new Error("Invalid stepIndex");

  const displayNum =
    typeof params.stepNumber === "number" && params.stepNumber > 0
      ? params.stepNumber
      : params.stepIndex + 1;
  const innerText = `${displayNum}.${params?.path
    ?.replaceAll("/#", "/")
    ?.replaceAll("@#", "/")}`;

  const output = outputList.find((item) => item?.index === params.stepIndex);
  const outputLists = output
    ? parseJSONWithStringNumbers(output.output)
    : undefined;

  params.tooltip =
    params.tooltip && params.tooltip !== ""
      ? params.tooltip
      : getTooltipforOutput(params.path?.substring(2), outputLists);
  params.tooltip =
    typeof params.tooltip === "object"
      ? JSON.stringify(params.tooltip)
      : params.tooltip;

  const span: HTMLSpanElement = createHTMLSpanElement(
    {
      name: params.name,
      type: "output",
      tooltip: params.tooltip,
      origId: params.origId,
    },
    false,
    count,
    innerText
  );
  setTagDisplayText(span, innerText);

  if (!isNotFirstRender) {
    const { isValidTag } = validateOutputTag(
      `/${params.stepIndex}/#${params.path.substring(2)}`,
      outputList
    );
    span.classList.toggle("output-tag", isValidTag);
    span.classList.toggle("invalid-output-tag", !isValidTag);
  }

  return span;
}

export function addMetaoutput(
  params: SpanParams,
  metaoutputList: any[],
  count: number,
  isNotFirstRender = true
): HTMLSpanElement {
  if (params.stepIndex < 0) throw new Error("Invalid stepIndex");

  const displayNum =
    typeof params.stepNumber === "number" && params.stepNumber > 0
      ? params.stepNumber
      : params.stepIndex + 1;
  const innerText = `${displayNum}.${params.path
    ?.replaceAll("/#", "/")
    ?.replaceAll("@#", "/")}`;

  const parts = params.name?.split("/");
  const lastBit = parts[parts.length - 1];
  const tagName = `/metaoutput/#ix-${params.stepIndex}@${lastBit}`;
  const { isValidTag, tooltipToUse } = validateMetaoutputTag(
    tagName,
    metaoutputList
  );

  const span: HTMLSpanElement = createHTMLSpanElement(
    {
      name: tagName,
      type: "metaoutput",
      tooltip: tooltipToUse,
      origId: params.origId,
    },
    false,
    count,
    innerText
  );

  setTagDisplayText(span, innerText);

  if (!isNotFirstRender) {
    span.classList.toggle("metaoutput-tag", isValidTag);
    span.classList.toggle("invalid-metaoutput-tag", !isValidTag);
  }

  return span;
}

export function addUserVariable(
  params: SpanParams,
  userDefinedVariablesList: any[],
  count: number,
  isNotFirstRender = true
): HTMLSpanElement {
  const innerText = `.${params.path
    ?.replaceAll("/#", "/")
    ?.replaceAll("@#", "/")}`;
  const rawTooltip =
    params.tooltip && params.tooltip !== ""
      ? params.tooltip
      : getTooltipForUdv(params.path?.substring(2), userDefinedVariablesList);
  const tooltip =
    typeof rawTooltip === "object" ? JSON.stringify(rawTooltip) : rawTooltip;

  const span = createHTMLSpanElement(
    {
      name: `/userDefinedVariables${params.name}`,
      type: "user-defined-variable",
      tooltip,
    },
    false,
    count,
    innerText
  );
  setTagDisplayText(span, innerText);
  return span;
}

export function addSystemVariable(
  params: SpanParams,
  systemVariables: Record<string, string>,
  count: number
): HTMLSpanElement {
  const rawTooltip =
    params.tooltip && params.tooltip !== ""
      ? params.tooltip
      : systemVariables?.[params.name] || "";
  const tooltip =
    typeof rawTooltip === "object" ? JSON.stringify(rawTooltip) : rawTooltip;

  const span = createHTMLSpanElement(
    { name: params.name, type: "system-variable", tooltip },
    false,
    count,
    true
  );
  setTagDisplayText(span, labelConvert(params.name));

  return span;
}

export function addFunction(
  item: {
    hasChild: boolean;
    type: string;
    name: string;
    function?: string;
    childs?: any[];
  },
  count: number
): HTMLSpanElement[] {
  const spans: HTMLSpanElement[] = [];
  let id = count;

  const main = createHTMLSpanElement(item, hasChild(item?.type), id, true);
  spans.push(main);

  if (item.hasChild && Array.isArray(item.childs)) {
    item.childs.forEach((child) => {
      id += 1;
      const childSpan = createHTMLSpanElement(child, false, id, true);
      childSpan.classList.add(getType(item?.type));
      spans.push(childSpan);
    });
  }

  return spans;
}
